import React, { useCallback, useEffect } from 'react'
import { useField } from 'formik'
import { FormControl, InputLabel, Select, MenuItem, Typography, FormHelperText, SelectChangeEvent } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { StaffData } from './StaffSelectionTable'

type StaffSelectFieldProps = {
    name: string
    label?: string
    staffs: StaffData[]
    disabled?: boolean
    readOnly?: boolean
    fullWidth?: boolean
}

export default function StaffSelectField(props: StaffSelectFieldProps) {
    const { staffs, disabled, readOnly, name, label, fullWidth } = props
    const { t } = useTranslation()
    const [field, meta, helpers] = useField(name)

    // Convert object to string ID if needed
    useEffect(() => {
        if (field.value && typeof field.value === 'object') {
            helpers.setValue(field.value.id || '')
        }
    }, [field.value, helpers])

    const handleChange = useCallback((event: SelectChangeEvent) => {
        helpers.setValue(event.target.value)
    }, [helpers])

    // Ensure the value is a string
    const value = typeof field.value === 'object' ?
        (field.value?.id || '') :
        (field.value || '')

    return (
        <FormControl
            fullWidth={fullWidth}
            error={!!meta.error && meta.touched && !readOnly && !disabled}
        >
            {label && <InputLabel>{label}</InputLabel>}
            <Select
                {...field}
                value={value}
                onChange={handleChange}
                disabled={disabled || readOnly}
                label={label}
            >
                {staffs.map(staff => (
                    <MenuItem
                        key={staff.id}
                        value={staff.id}
                        disabled={!staff.stripe}
                        sx={{
                            color: !staff.stripe ? 'text.disabled' : 'text.primary'
                        }}
                    >
                        <Typography>
                            {`${staff.name} ${staff.surname}`}
                            {!staff.stripe && ` (${t('form.paymentConfig.stripeNotConfigured')})`}
                        </Typography>
                    </MenuItem>
                ))}
            </Select>
            {!!meta.error && meta.touched && !readOnly && !disabled && (
                <FormHelperText>
                    {typeof meta.error === 'string'
                        ? meta.error
                        : typeof meta.error === 'object' && meta.error !== null
                            ? JSON.stringify(meta.error)
                            : 'Invalid field'}
                </FormHelperText>
            )}
        </FormControl>
    )
} 